// Remediation — data model, API mapping & suggestion engine (from
// design-reference/remediation/data.jsx). Work items persist via
// src/worker/modules/remediation.ts; this module maps the wire DTO ↔ RemItem
// and hosts the AI suggestion/drafting helpers the wizard + Overview consume.
import type { ApiRemItem } from "../data/api";
import { catOf } from "../defects/defect-meta";
import type { Defect } from "../defects/types";

export type RemPriority = "p1" | "p2" | "p3" | "p4";
export type RemStatus = "proposed" | "approved" | "scheduled" | "complete";

export interface RemCosts {
  labour: number;
  materials: number;
  access: number;
  other: number;
  contPct: number;
  hours?: string;
}

export interface RemItem {
  id: string;
  ref: string; // REM-NN, sequential per inspection
  title: string;
  scope?: string;
  priority: RemPriority;
  status: RemStatus;
  trade?: string; // legacy single-trade shape
  trades?: string[];
  defectIds: string[];
  costs: RemCosts | null;
  createdAt: number;
  by?: string;
  source?: "ai" | "user";
  conf?: number;
}

// Priority grading follows condition-survey practice: a grade plus a
// recommended action timeframe the client can plan against.
export const R_PRIORITY: Record<RemPriority, { label: string; time: string; color: string; bg: string; bc: string; rank: number }> = {
  p1: { label: "Immediate", time: "Act immediately", color: "#EF4444", bg: "rgba(239,68,68,0.13)", bc: "rgba(239,68,68,0.34)", rank: 4 },
  p2: { label: "Urgent", time: "Within 30 days", color: "#F97316", bg: "rgba(249,115,22,0.12)", bc: "rgba(249,115,22,0.32)", rank: 3 },
  p3: { label: "Planned", time: "Within 90 days", color: "#F5B544", bg: "rgba(245,181,68,0.12)", bc: "rgba(245,181,68,0.30)", rank: 2 },
  p4: { label: "Monitor", time: "Next inspection cycle", color: "#7E8896", bg: "rgba(126,136,150,0.14)", bc: "rgba(126,136,150,0.30)", rank: 1 },
};
export const R_PRI_ORDER: RemPriority[] = ["p1", "p2", "p3", "p4"];

export const R_STATUS: Record<RemStatus, { label: string; color: string; bg: string; bc: string }> = {
  proposed: { label: "Proposed", color: "#9AA4B2", bg: "rgba(154,164,178,0.10)", bc: "rgba(154,164,178,0.28)" },
  approved: { label: "Approved", color: "#6AA6FF", bg: "rgba(106,166,255,0.10)", bc: "rgba(106,166,255,0.28)" },
  scheduled: { label: "Scheduled", color: "#F5B544", bg: "rgba(245,181,68,0.10)", bc: "rgba(245,181,68,0.28)" },
  complete: { label: "Complete", color: "#4ED08A", bg: "rgba(78,208,138,0.10)", bc: "rgba(78,208,138,0.28)" },
};
export const R_STATUS_ORDER: RemStatus[] = ["proposed", "approved", "scheduled", "complete"];

export const R_TRADES = ["Roofing contractor", "Waterproofing specialist", "Corrosion treatment", "Gutter & drainage", "General maintenance", "Specialist access", "Other"];

export const R_COST_FIELDS: { id: keyof Pick<RemCosts, "labour" | "materials" | "access" | "other">; label: string }[] = [
  { id: "labour", label: "Labour" },
  { id: "materials", label: "Materials" },
  { id: "access", label: "Plant & access" },
  { id: "other", label: "Other costs" },
];

// Items may carry several trades. Legacy items store a single `trade`
// string; these helpers normalise both shapes.
export const remTrades = (it: RemItem | null | undefined): string[] =>
  it && it.trades && it.trades.length ? it.trades : it && it.trade ? [it.trade] : [];
export const remTradesLabel = (it: RemItem | null | undefined): string => {
  const t = remTrades(it);
  return t.length ? t.join(" · ") : "—";
};

// ── money helpers ─────────────────────────────────────────────────────
export const remBase = (c: RemCosts | null | undefined): number =>
  c ? (c.labour || 0) + (c.materials || 0) + (c.access || 0) + (c.other || 0) : 0;
export const remTotal = (it: Pick<RemItem, "costs">): number => {
  const b = remBase(it.costs);
  return Math.round(b * (1 + ((it.costs && it.costs.contPct) || 0) / 100));
};
export const fmtR = (n: number): string => "R " + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export function nextRemRef(items: RemItem[] | null | undefined): string {
  const max = (items || []).reduce((a, it) => {
    const m = /^REM-(\d+)$/.exec(it.ref || "");
    return m ? Math.max(a, parseInt(m[1]!, 10)) : a;
  }, 0);
  return "REM-" + String(max + 1).padStart(2, "0");
}

// ── AI suggestion engine ──────────────────────────────────────────────
// Groups detected defects by repair method (category families), matches a
// scope of work, and prices it: base rate plus a per-defect increment.
interface SuggestGroup {
  key: string;
  cats: string[];
  title: string;
  priority: RemPriority;
  trade: string;
  conf: number;
  scope: string;
  cost: { labour: [number, number]; materials: [number, number]; access: [number, number]; other: [number, number]; contPct: number } | null;
}
// Category families follow CAT_FAMILIES (defects/meta.tsx); the 19-value
// taxonomy is grouped into repair methods here.
const R_SUGGEST_GROUPS: SuggestGroup[] = [
  { key: "seals", cats: ["damaged_flashing", "sealant_failure"], title: "Re-seal ridge and lap joints", priority: "p2", trade: "Roofing contractor", conf: 94,
    scope: "Strip and re-dress the lifted ridge flashing, rake out perished lap sealant and re-seal with butyl tape and MS polymer.",
    cost: { labour: [12800, 2800], materials: [6200, 1700], access: [7500, 0], other: [1200, 0], contPct: 10 } },
  { key: "structural", cats: ["crack", "spall"], title: "Repair cracking and spalling", priority: "p2", trade: "General maintenance", conf: 90,
    scope: "Rake out and fill cracked sections, break back spalled concrete to sound substrate, treat exposed reinforcement and reinstate with a polymer-modified repair mortar.",
    cost: { labour: [8800, 2100], materials: [4600, 900], access: [5200, 0], other: [800, 0], contPct: 10 } },
  { key: "fasteners", cats: ["fasteners"], title: "Refix and replace sheet fasteners", priority: "p2", trade: "Roofing contractor", conf: 91,
    scope: "Replace lifted and missing fixings with class 4 self-drilling screws, oversize where holes are stripped, and torque-check the adjacent fastener runs.",
    cost: { labour: [5400, 1400], materials: [2100, 300], access: [3200, 0], other: [600, 0], contPct: 5 } },
  { key: "corrosion", cats: ["corrosion", "coating_failure", "staining"], title: "Treat corrosion and recoat affected sheets", priority: "p3", trade: "Corrosion treatment", conf: 88,
    scope: "Mechanically prepare the corroded and stained areas, apply rust converter and a two-part epoxy primer, then recoat with a colour-matched topcoat system.",
    cost: { labour: [9800, 2200], materials: [6400, 1200], access: [5400, 0], other: [900, 0], contPct: 10 } },
  { key: "biological", cats: ["biological_growth", "vegetation"], title: "Soft-wash growth and clear vegetation", priority: "p3", trade: "General maintenance", conf: 85,
    scope: "Soft-wash the affected surfaces with biocide, remove established vegetation including roots, and flush the adjacent drainage paths.",
    cost: { labour: [5600, 1200], materials: [1800, 300], access: [3400, 0], other: [500, 0], contPct: 10 } },
  { key: "drainage", cats: ["ponding", "debris", "abandoned_material"], title: "Clear drainage lines and correct ponding falls", priority: "p3", trade: "Gutter & drainage", conf: 86,
    scope: "Clear gutters, outlets and downpipes, remove accumulated material, then pack the deflected sheet bay at purlin level to restore fall and prevent standing water.",
    cost: { labour: [6200, 1300], materials: [3400, 500], access: [3600, 0], other: [800, 0], contPct: 10 } },
  { key: "monitor", cats: ["deformation"], title: "Monitor sheet deformation", priority: "p4", trade: "General maintenance", conf: 78,
    scope: "No intervention required now. Photograph and re-measure the affected sheet at the next inspection cycle and escalate if the deflection progresses.",
    cost: null },
];

export interface RemSuggestion {
  key: string;
  title: string;
  priority: RemPriority;
  trade: string;
  conf: number;
  scope: string;
  defects: Defect[];
  costs: RemCosts;
  coveredCount: number;
  allCovered: boolean;
}

export function remSuggestions(defects: Defect[] | null | undefined, items: RemItem[] | null | undefined): RemSuggestion[] {
  const covered = new Set<string>();
  (items || []).forEach((it) => (it.defectIds || []).forEach((id) => covered.add(id)));
  return R_SUGGEST_GROUPS.map((g) => {
    const matched = (defects || []).filter((d) => g.cats.includes(catOf(d)));
    if (!matched.length) return null;
    const n = matched.length;
    const costs: RemCosts = g.cost
      ? { labour: g.cost.labour[0] + g.cost.labour[1] * (n - 1), materials: g.cost.materials[0] + g.cost.materials[1] * (n - 1),
          access: g.cost.access[0], other: g.cost.other[0], contPct: g.cost.contPct }
      : { labour: 0, materials: 0, access: 0, other: 0, contPct: 0 };
    const coveredCount = matched.filter((d) => covered.has(d.id)).length;
    return { key: g.key, title: g.title, priority: g.priority, trade: g.trade, conf: g.conf, scope: g.scope,
      defects: matched, costs, coveredCount, allCovered: coveredCount === n };
  }).filter((v): v is RemSuggestion => !!v);
}

// ── AI drafting for a chosen defect set ───────────────────────────────
export interface RemDraft {
  title: string;
  priority: RemPriority;
  trade: string;
  trades: string[];
  conf: number;
  scope: string;
  costs: RemCosts;
}
export function remDraftFromDefects(defects: Defect[] | null | undefined): RemDraft {
  const scored = R_SUGGEST_GROUPS
    .map((g) => ({ g, hit: (defects || []).filter((d) => g.cats.includes(catOf(d))).length }))
    .filter((x) => x.hit > 0)
    .sort((a, b) => b.hit - a.hit);
  if (!scored.length) {
    return { title: "General repair works", priority: "p3", trade: "General maintenance", trades: ["General maintenance"], conf: 72,
      scope: "Carry out corrective repairs to the linked defects, make good the surrounding surfaces, and provide photographic evidence of completion.",
      costs: { labour: 4800, materials: 2200, access: 2600, other: 500, contPct: 10 } };
  }
  const top = scored[0]!;
  const extras = scored.slice(1);
  const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
  let scope = top.g.scope;
  if (extras.length) scope += " In the same visit, " + extras.map((x) => lower(x.g.title)).join(", and ") + ".";
  const pri = scored.reduce<RemPriority>((best, x) => (R_PRIORITY[x.g.priority].rank > R_PRIORITY[best].rank ? x.g.priority : best), top.g.priority);
  const sum = { labour: 0, materials: 0, access: 0, other: 0 };
  let contPct = 0;
  scored.forEach((x, i) => {
    const c = x.g.cost;
    if (!c) return;
    sum.labour += c.labour[0] + c.labour[1] * (x.hit - 1);
    sum.materials += c.materials[0] + c.materials[1] * (x.hit - 1);
    sum.access += i === 0 ? c.access[0] : Math.round(c.access[0] * 0.4); // shared mobilisation and access
    sum.other += c.other[0];
    contPct = Math.max(contPct, c.contPct);
  });
  const tradeSet: string[] = [];
  scored.forEach((x) => { if (!tradeSet.includes(x.g.trade)) tradeSet.push(x.g.trade); });
  return {
    title: extras.length ? top.g.title + " and associated repairs" : top.g.title,
    priority: pri, trade: top.g.trade, trades: tradeSet.slice(0, 3), conf: Math.max(64, top.g.conf - extras.length * 4),
    scope, costs: { ...sum, contPct },
  };
}

// ── API DTO → RemItem ──────────────────────────────────────────────────
// The worker resolves the creator name (`by`) from master membership rows, so
// no client-side user lookup is needed.
export function remItemFromApi(d: ApiRemItem): RemItem {
  return {
    id: d.id,
    ref: d.ref,
    title: d.title,
    scope: d.scope,
    priority: d.priority,
    status: d.status,
    trades: d.trades,
    defectIds: d.defectIds,
    costs: d.costs,
    createdAt: d.createdAt,
    by: d.by || undefined,
    source: d.source,
    conf: d.conf ?? undefined,
  };
}
